import type React from "react";
import { useEffect } from "react";
import { bindActionCreators } from "redux";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";
import { connect } from "react-redux";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import { getHsMappingFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspotApi";
import { getSfMappingFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforceApi";
import { getDynamicsMappingFieldsThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamicsApi";
import { selectHsExcludeAccountFieldsRaw } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import { selectSfExcludeAccountFieldsRaw } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { selectDynamicsExcludeAccountFieldsRaw } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import {
    getHsExcludeAccountFieldsThunk,
    getHsExclusionStatusThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/exclude";
import {
    getSfExcludeAccountFieldsThunk,
    getSfExclusionFilterStatusThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/exclude";
import {
    getDynamicsExcludeAccountFieldsThunk,
    getDynamicsExclusionStatusThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/exclude";
import { useMount } from "app/custom-hooks/useMount";

import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

const mapStateToProps = (state: RootState) => {
    return {
        sfExcludeAccountFields: selectSfExcludeAccountFieldsRaw(state),
        hsExcludeAccountFields: selectHsExcludeAccountFieldsRaw(state),
        dynamicsExcludeAccountFields: selectDynamicsExcludeAccountFieldsRaw(state),
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatchCommon) => {
    return bindActionCreators(
        {
            getHsMappingFields: getHsMappingFieldsThunk,
            getHsExcludeAccountFields: getHsExcludeAccountFieldsThunk,
            getHsIntegrationLeadsFilterStatus: getHsExclusionStatusThunk,
            getSfMappingFields: getSfMappingFieldsThunk,
            getSfExcludeAccountFields: getSfExcludeAccountFieldsThunk,
            getSfIntegrationLeadsFilterStatus: getSfExclusionFilterStatusThunk,
            getDynamicsMappingFields: getDynamicsMappingFieldsThunk,
            getDynamicsExcludeAccountFields: getDynamicsExcludeAccountFieldsThunk,
            getDynamicsIntegrationLeadsFilterStatus: getDynamicsExclusionStatusThunk,
        },
        dispatch,
    );
};

const withAccountExclusionRequests = <PROPS extends object>(Component: React.ComponentType<PROPS>) => {
    const WrappedWithAccountExclusionRequests = (
        props: ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>,
    ) => {
        const { integrationName } = getIntegrationNameFromUrl();
        const {
            getHsMappingFields,
            getHsExcludeAccountFields,
            getHsIntegrationLeadsFilterStatus,
            getSfMappingFields,
            getSfExcludeAccountFields,
            getSfIntegrationLeadsFilterStatus,
            getDynamicsMappingFields,
            getDynamicsExcludeAccountFields,
            getDynamicsIntegrationLeadsFilterStatus,
            hsExcludeAccountFields,
            sfExcludeAccountFields,
            dynamicsExcludeAccountFields,
            ...rest
        } = props;

        useEffect(() => {
            if (integrationName === ExportIntegrations.salesforce) {
                getSfMappingFields();
            }

            if (integrationName === ExportIntegrations.hubspot) {
                getHsMappingFields();
            }

            if (integrationName === ExportIntegrations.dynamics) {
                getDynamicsMappingFields();
            }
        }, [getHsMappingFields, getSfMappingFields, getDynamicsMappingFields, integrationName]);

        useMount(() => {
            if (integrationName === ExportIntegrations.salesforce) {
                getSfIntegrationLeadsFilterStatus();

                if (!sfExcludeAccountFields.length) {
                    getSfExcludeAccountFields();
                }
            }

            if (integrationName === ExportIntegrations.hubspot) {
                getHsIntegrationLeadsFilterStatus();

                if (!hsExcludeAccountFields.length) {
                    getHsExcludeAccountFields();
                }
            }

            if (integrationName === ExportIntegrations.dynamics) {
                getDynamicsIntegrationLeadsFilterStatus();

                if (!dynamicsExcludeAccountFields.length) {
                    getDynamicsExcludeAccountFields();
                }
            }
        });

        return <Component {...(rest as unknown as PROPS)} />;
    };

    return connect(
        mapStateToProps,
        mapDispatchToProps,
    )(WrappedWithAccountExclusionRequests) as React.ComponentType<PROPS>;
};

export default withAccountExclusionRequests;
